import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

export type BjStatus = "ideal" | "di_bawah" | "di_atas" | null;
export type BjVerdikt = "efisien" | "tidak_efisien" | "data_tidak_cukup";

export type BjKendala =
  | "bor_min"
  | "bor_max"
  | "alos_min"
  | "alos_max"
  | "toi_min"
  | "toi_max"
  | "bto_min";

export interface BjJendelaAlos {
  bawah: number;
  atas: number;
}

export interface BjIndikatorItem {
  nilai: number | null;
  ideal: string;
  status: BjStatus;
  jendela?: BjJendelaAlos | null;
}

export interface BjAnalisis {
  indikator: Record<"bor" | "alos" | "toi" | "bto", BjIndikatorItem>;
  kendala_gagal: BjKendala[];
  di_zona: boolean | null;
  verdikt: BjVerdikt;
  bor_geometris: number | null;
  selisih_bor: number | null;
}

export interface BjTitik {
  toi: number | null;
  alos: number | null;
  bor: number | null;
  bto_tahunan: number | null;
  di_zona: boolean | null;
  kendala_gagal: BjKendala[];
}

export interface BjTitikRs extends BjTitik {
  label: string;
}

export interface BjTitikUnit extends BjTitik {
  unit_kode: string | number;
  unit_nama: string;
  tt: number;
  hp: number;
  ld: number;
  pk: number;
  bto_periode: number | null;
}

export interface BjKoordinat {
  toi: number;
  alos: number;
}

export interface BjSudut extends BjKoordinat {
  label: string;
}

export interface BjZona {
  ambang: {
    bor_min: number;
    bor_max: number;
    alos_min: number;
    alos_max: number;
    toi_min: number;
    toi_max: number;
    bto_min_tahunan: number;
  };
  amplop: { x: number[]; bawah: number[]; atas: number[] };
  sudut: BjSudut[];
  garis: {
    bor_min: BjKoordinat[];
    bor_max: BjKoordinat[];
    bto_min: BjKoordinat[];
  };
}

export interface BjUnitTanpaTt {
  unit_kode: string | number;
  unit_nama: string;
  hp: number;
  pk: number;
}

export interface BarberJohnsonResponse {
  periode: { start: string; end: string; days: number; label: string };
  kapasitas_tt: {
    total: number;
    per_unit: Record<string, number>;
    sumber: string;
    catatan: string;
    unit_tanpa_tt: BjUnitTanpaTt[];
  };
  agregat: {
    hp: number;
    ld: number;
    pk: number;
    pk_hidup: number;
    mati_total: number;
    mati_ge48: number;
    tt_x_t: number;
  };
  indikator: {
    bor: number | null;
    alos: number | null;
    toi: number | null;
    bto_periode: number | null;
    bto_tahunan: number | null;
  };
  analisis: BjAnalisis;
  zona: BjZona;
  titik: { rs: BjTitikRs; per_unit: BjTitikUnit[] };
  params: Record<string, unknown>;
}

export interface BarberJohnsonFilter {
  periode_start: string; // YYYY-MM-DD
  periode_end: string; // YYYY-MM-DD
  unit?: string[];
}

async function preview(
  filter: BarberJohnsonFilter
): Promise<BarberJohnsonResponse> {
  const res = await http
    .getDataParam(`/laporan/barber-johnson`, {
      params: {
        periode_start: filter.periode_start,
        periode_end: filter.periode_end,
        unit: filter.unit
      }
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function openPdf(
  filter: BarberJohnsonFilter,
  grafikBase64: string | null
): Promise<void> {
  const res = await http
    .postData(
      `/laporan/barber-johnson/pdf`,
      {
        periode_start: filter.periode_start,
        periode_end: filter.periode_end,
        unit: filter.unit ?? [],
        grafik_base64: grafikBase64
      },
      { responseType: "blob" }
    )
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });

  const url = URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  );
  window.open(url, "_blank");
  // Beri jeda agar tab sempat memuat sebelum object URL dilepas.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default { preview, openPdf };
